import { Box, Fade, Tooltip, Typography } from '@mui/material'
import theme from 'lib/theme'
import { useEffect, useRef, useState } from 'react'
import { PickingStatus, Product, ProductInfo } from 'types/model/product'
import {
  ProductColWrapper,
  ProductName,
  TooltipStyling,
} from './OrderProduct.styled'
import { ProductColPills } from './ProductColPills'

export const ProductColumn = ({
  productInfo,
  product,
  offerName,
}: {
  productInfo: ProductInfo[]
  product: Product
  offerName?: string
}) => {
  const productNamesRef = useRef<HTMLParagraphElement | null>(null)
  // hover true if there is an ellipsis
  const [hoverStatus, setHoverStatus] = useState(false)
  const details = productInfo.filter(
    (p) => p.extId === product.sku || p.gtin === product.gtin.toString()
  )[0]
  const substitutedProductName = productInfo.filter(
    (p) =>
      p.extId === (product.collectedProduct && product.collectedProduct.sku) ||
      p.gtin ===
        (product.collectedProduct && product.collectedProduct.gtin.toString())
  )[0]?.name
  const substitutedProductGtin = productInfo.filter(
    (p) =>
      p.extId === (product.collectedProduct && product.collectedProduct.sku) ||
      p.gtin ===
        (product.collectedProduct && product.collectedProduct.gtin.toString())
  )[0]?.gtin
  const substituted =
    product.pickingStatus.toLowerCase() === PickingStatus.SUBSTITUTED

  // product pill info
  const { extraProduct, fulfilmentFlags } = product
  // checks whether the text overflows and there is an ellipsis
  const checkTextOverflow = () => {
    let compare = false
    if (productNamesRef !== null && productNamesRef.current !== null) {
      compare = Boolean(
        productNamesRef.current &&
          productNamesRef.current.scrollWidth >
            productNamesRef.current.clientWidth
      )
    }
    setHoverStatus(compare)
  }

  useEffect(() => {
    checkTextOverflow()
    window.addEventListener('resize', checkTextOverflow)
    return () => {
      window.removeEventListener('resize', checkTextOverflow)
    }
  }, [])

  return details && (details.name || details.details.brandName) ? (
    <Box>
      {substituted && (
        <ProductColWrapper>
          <Box
            sx={{
              maxWidth: '200px',
              height: '100%',
              whiteSpace: 'nowrap',
            }}>
            <Box display='flex' alignItems='center'>
              <Tooltip
                TransitionComponent={Fade}
                title={
                  <Typography
                    variant='body1'
                    sx={{
                      margin: theme.spacing(0.5),
                      color: theme.palette.text.primary,
                    }}>
                    {substitutedProductName}
                  </Typography>
                }
                componentsProps={{
                  tooltip: {
                    sx: TooltipStyling,
                  },
                }}>
                <Typography
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '&:hover': { cursor: 'pointer' },
                  }}>
                  {substitutedProductName}
                </Typography>
              </Tooltip>
              <ProductColPills
                extraProduct={extraProduct}
                fulfilmentFlags={fulfilmentFlags}
              />
            </Box>
            <Typography sx={{ color: theme.palette.text.secondary }}>
              {substitutedProductGtin || product.collectedProduct.gtin}
            </Typography>
          </Box>
        </ProductColWrapper>
      )}
      <ProductColWrapper>
        <Box
          sx={{
            height: '100%',
            whiteSpace: 'nowrap',
          }}>
          <Box display='flex' alignItems='center'>
            <Tooltip
              TransitionComponent={Fade}
              disableHoverListener={!hoverStatus}
              title={
                <Typography
                  variant='body1'
                  sx={{
                    margin: theme.spacing(0.5),
                    color: theme.palette.text.primary,
                  }}>
                  {details.name}
                </Typography>
              }
              componentsProps={{
                tooltip: {
                  sx: TooltipStyling,
                },
              }}>
              <ProductName
                variant='body1'
                ref={productNamesRef}
                hoverStatus={hoverStatus}
                substituted={substituted}>
                {details.name}
              </ProductName>
            </Tooltip>
            {!substituted && (
              <ProductColPills
                extraProduct={extraProduct}
                fulfilmentFlags={fulfilmentFlags}
              />
            )}
          </Box>
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              textDecoration: substituted ? 'line-through' : 'none',
            }}>
            {details.gtin || product.gtin}
          </Typography>
          {offerName && (
            <Typography
              sx={{
                color: theme.palette.error.main,
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                gap: theme.spacing(0.5),
              }}>
              <svg
                width='18'
                height='18'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'>
                <path
                  d='M13.7879 2.19224C14.7103 1.79614 15.7835 2.14519 16.2963 3.00814L17.29 4.68L19.1902 5.11162C20.1687 5.33388 20.831 6.24678 20.7387 7.24594L20.56 9.18L21.842 10.6407C22.5045 11.3955 22.5045 12.5245 21.842 13.2793L20.56 14.74L20.7387 16.6741C20.831 17.6732 20.1687 18.5861 19.1902 18.8084L17.29 19.24L16.2963 20.9119C15.7835 21.7748 14.7103 22.1239 13.7879 21.7278L12 20.96L10.2144 21.732C9.29121 22.1312 8.21525 21.7827 7.7014 20.9181L6.71 19.25L4.80939 18.8183C3.83108 18.5961 3.16878 17.6835 3.26083 16.6845L3.44 14.74L2.15587 13.2769C1.49429 12.5232 1.49328 11.396 2.15352 10.641L3.44 9.17L3.26173 7.24058C3.16924 6.23943 3.83436 5.32523 4.81539 5.10511L6.71 4.68L7.70365 3.00814C8.21653 2.14519 9.28965 1.79614 10.2121 2.19224L12 2.96L13.7879 2.19224ZM14.58 4.03L12 5.14L9.42 4.04L7.99 6.45L5.25 7.06L5.51 9.84L3.66 11.96L5.51 14.06L5.25 16.86L7.99 17.48L9.42 19.89L12 18.78L14.58 19.89L16.01 17.48L18.75 16.86L18.49 14.07L20.34 11.96L18.49 9.85L18.75 7.06L16.01 6.44L14.58 4.03ZM14.2864 13.3673C14.9807 13.3673 15.5436 13.9302 15.5436 14.6245C15.5436 15.3189 14.9807 15.8818 14.2864 15.8818C13.592 15.8818 13.0291 15.3189 13.0291 14.6245C13.0291 13.9302 13.592 13.3673 14.2864 13.3673ZM15.2 7.5L16.3818 8.68184L9.18183 15.8818L8 14.7L15.2 7.5ZM10.0955 7.5C10.7898 7.5 11.3527 8.0629 11.3527 8.75727C11.3527 9.45165 10.7898 10.0145 10.0955 10.0145C9.40108 10.0145 8.83818 9.45165 8.83818 8.75727C8.83818 8.0629 9.40108 7.5 10.0955 7.5Z'
                  fill='currentColor'
                />
              </svg>
              {offerName}
            </Typography>
          )}
        </Box>
      </ProductColWrapper>
    </Box>
  ) : (
    <Box />
  )
}
